import { useMemo } from "react";
import { fmt, YARD_SCALE as SCALE, makeDefaultTables, overlapIds, layoutTablePositions } from "../lib/calc";
import { useDrag, useDropZone } from "../context/DragContext";
import { RotateCw, X, Map, Pencil, Boxes, Eraser, AlertTriangle, Wand2 } from "lucide-react";

export function YardPlanner({ tables, cols = 1, panelsById, placements, onPlace, onMove, onRotate, onRemove, onEditTables, onPackYard, onClearTables, onResolveOverlaps }) {
  const tbls = tables && tables.length ? tables : makeDefaultTables();
  const nCols = Math.min(4, Math.max(1, cols || 1));
  const positioned = useMemo(() => layoutTablePositions(tbls, nCols), [tbls, nCols]);
  const bounds = useMemo(
    () => positioned.reduce((a, t) => ({ w: Math.max(a.w, t.x + t.w), h: Math.max(a.h, t.y + t.h) }), { w: 0, h: 0 }),
    [positioned]
  );

  const overlapSet = useMemo(() => {
    const s = new Set();
    tbls.forEach((t) => {
      overlapIds(placements.filter((p) => p.table === t.id), panelsById).forEach((id) => s.add(id));
    });
    return s;
  }, [tbls, placements, panelsById]);

  return (
    <div className="bg-[#16181A] border border-[#2E3033]">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-[#2E3033] flex-wrap">
        <Map className="w-4 h-4 text-[#2560C9]" />
        <h2 className="font-heading font-bold text-white text-sm tracking-tight">YARD PLANNER</h2>
        <span className="text-[10px] font-mono text-[#6E7787] ml-1">1 cell = 1 m · drag panels here</span>
        <div className="ml-auto flex items-center gap-1.5">
          <button
            data-testid="pack-yard-button"
            onClick={onPackYard}
            className="flex items-center gap-1 bg-[#2560C9] hover:bg-[#3B75DF] text-white text-xs px-2.5 py-1.5 transition-colors"
            title="Auto-pack unplaced panels onto the tables (grouped by strength)"
          >
            <Boxes className="w-3.5 h-3.5" /> Pack Yard
          </button>
          <button
            data-testid="clear-tables-button"
            onClick={onClearTables}
            className="flex items-center gap-1 border border-[#2E3033] hover:border-[#EF4444] hover:text-[#EF4444] text-[#A0AABF] text-xs px-2.5 py-1.5 transition-colors"
            title="Remove all panels from the tables"
          >
            <Eraser className="w-3.5 h-3.5" /> Clear
          </button>
          <button
            data-testid="edit-tables-button"
            onClick={onEditTables}
            className="flex items-center gap-1 border border-[#2E3033] hover:border-[#2560C9] text-[#A0AABF] hover:text-white text-xs px-2.5 py-1.5 transition-colors"
            title="Edit yard layout (sizes / add / remove tables)"
          >
            <Pencil className="w-3.5 h-3.5" /> Edit Layout
          </button>
        </div>
      </div>

      {overlapSet.size > 0 && (
        <div
          data-testid="overlap-warning"
          className="flex items-center gap-2 mx-4 mt-3 px-3 py-2 bg-[#FF3B30]/10 border border-[#FF3B30]/50"
        >
          <AlertTriangle className="w-4 h-4 text-[#FF3B30] shrink-0" />
          <span className="text-[11px] font-mono text-[#FF3B30]">
            {overlapSet.size} panel{overlapSet.size !== 1 ? "s" : ""} overlapping — highlighted in red.
          </span>
          <button
            data-testid="resolve-overlaps-button"
            onClick={onResolveOverlaps}
            className="ml-auto flex items-center gap-1 bg-[#FF3B30] hover:bg-[#ff5146] text-white text-[10px] font-mono uppercase tracking-wider px-2.5 py-1 transition-colors"
            title="Automatically nudge the overlapping panels apart (reuses the Pack Yard packer)"
          >
            <Wand2 className="w-3 h-3" /> Fix Overlaps
          </button>
        </div>
      )}

      <div className="p-4 overflow-auto">
        <div className="relative" style={{ width: bounds.w * SCALE + 8, height: bounds.h * SCALE + 28, paddingTop: 20 }}>
          {positioned.map((t) => (
            <div key={t.id} className="absolute" style={{ left: t.x * SCALE, top: t.y * SCALE + 20 }}>
              <Table
                table={t}
                panelsById={panelsById}
                placements={placements.filter((p) => p.table === t.id)}
                overlapSet={overlapSet}
                onPlace={onPlace}
                onMove={onMove}
                onRotate={onRotate}
                onRemove={onRemove}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function dims(panel, rotated) {
  const hMeters = rotated ? panel.width : panel.length;
  const vMeters = rotated ? panel.length : panel.width;
  return { hMeters, vMeters };
}

function Table({ table, panelsById, placements, overlapSet, onPlace, onMove, onRotate, onRemove }) {
  const { startDrag } = useDrag();

  const handleDrop = (payload, info) => {
    const rect = info.rect;
    const mx = (info.clientX - rect.left) / SCALE;
    const my = (info.clientY - rect.top) / SCALE;

    if (payload.source === "queue") {
      const panel = panelsById[payload.panelId];
      if (!panel) return;
      const { hMeters, vMeters } = dims(panel, false);
      const x = clamp(mx - hMeters / 2, 0, table.w - hMeters);
      const y = clamp(my - vMeters / 2, 0, table.h - vMeters);
      onPlace({ panelId: payload.panelId, table: table.id, x, y, rotated: false });
    } else if (payload.source === "placement") {
      const panel = panelsById[payload.panelId];
      if (!panel) return;
      const { hMeters, vMeters } = dims(panel, payload.rotated);
      const x = clamp(mx - hMeters / 2, 0, table.w - hMeters);
      const y = clamp(my - vMeters / 2, 0, table.h - vMeters);
      onMove(payload.placementId, { table: table.id, x, y });
    }
  };

  const attach = useDropZone(handleDrop);

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="font-heading font-bold text-white text-xs tracking-[0.15em]">{table.label}</span>
        <span className="text-[10px] font-mono text-[#6E7787]">
          {fmt(table.w)} m × {fmt(table.h)} m
        </span>
      </div>
      <div
        ref={attach}
        data-testid={`yard-table-${table.id}`}
        className="yard-grid relative bg-[#0A0A0A] border border-[#2E3033]"
        style={{ width: table.w * SCALE, height: table.h * SCALE, "--cell": `${SCALE}px`, touchAction: "none" }}
      >
        {placements.map((pl) => {
          const panel = panelsById[pl.panelId];
          if (!panel) return null;
          const { hMeters, vMeters } = dims(panel, pl.rotated);
          const clashing = overlapSet?.has(pl.id);
          const ghost = (
            <div
              style={{
                width: Math.max(hMeters * SCALE, 10),
                height: Math.max(vMeters * SCALE, 10),
                backgroundColor: `${panel.color}dd`,
                border: `1.5px solid ${panel.color}`,
              }}
            />
          );
          return (
            <div
              key={pl.id}
              data-testid={`placed-panel-${pl.id}`}
              data-overlap={clashing ? "true" : "false"}
              onPointerDown={(e) =>
                startDrag({ source: "placement", placementId: pl.id, panelId: pl.panelId, rotated: pl.rotated }, ghost, e)
              }
              className={`group absolute flex items-center justify-center overflow-hidden cursor-grab active:cursor-grabbing shadow-md ${clashing ? "z-20" : ""}`}
              style={{
                left: pl.x * SCALE,
                top: pl.y * SCALE,
                width: Math.max(hMeters * SCALE, 8),
                height: Math.max(vMeters * SCALE, 8),
                backgroundColor: clashing ? "#FF3B30cc" : `${panel.color}cc`,
                border: clashing ? "2px solid #FF3B30" : `1.5px solid ${panel.color}`,
                boxShadow: clashing ? "0 0 0 2px rgba(255,59,48,0.4)" : undefined,
                touchAction: "none",
              }}
              title={clashing ? `${panel.name} — OVERLAPPING another panel` : `${panel.name} — ${fmt(panel.width)}×${fmt(panel.length)}m`}
            >
              {clashing && (
                <span className="absolute top-0.5 left-0.5 pointer-events-none">
                  <AlertTriangle className="w-3 h-3 text-white drop-shadow" />
                </span>
              )}
              <span className="text-[9px] font-mono text-white font-bold truncate px-1 pointer-events-none select-none">
                {panel.name}
              </span>
              <div className="absolute top-0.5 right-0.5 flex gap-0.5 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                <button
                  data-testid={`rotate-placed-${pl.id}`}
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => { e.stopPropagation(); onRotate(pl.id); }}
                  className="bg-black/70 hover:bg-[#2560C9] text-white p-0.5"
                >
                  <RotateCw className="w-3 h-3" />
                </button>
                <button
                  data-testid={`remove-placed-${pl.id}`}
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => { e.stopPropagation(); onRemove(pl.id); }}
                  className="bg-black/70 hover:bg-[#EF4444] text-white p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function clamp(v, min, max) {
  const hi = max < min ? min : max;
  return Math.max(min, Math.min(hi, v));
}
